import {
  LABEL_VARIANTS,
  SIDEBAR_SPRING,
} from '@src/components/sidebar/sidebarMotion';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useId, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from './tooltipStyles';

import type { CSSProperties, ReactNode } from 'react';

export interface Tab<TId extends string = string> {
  id: TId;
  label: string;
  /** Optional icon. Required for the `icon-only` variant to render usefully. */
  icon?: ReactNode;
  content?: ReactNode;
  /** data-testid applied to this tab's button. */
  testId?: string;
}

export type TabsVariant = 'default' | 'icon-only';

export interface TabsProps<TId extends string = string> {
  /** Array of tab definitions */
  tabs: Tab<TId>[];
  /**
   * Visual variant.
   * - `default`: icon (optional) + label, underline indicator on the active tab.
   * - `icon-only`: icons only; inactive tabs show a tooltip on hover, the active
   *   tab expands to icon + label (Notion-style). Use where horizontal space is
   *   limited.
   */
  variant?: TabsVariant;
  /** Controlled active tab id. Omit for uncontrolled (internal state). */
  activeTab?: TId;
  /** Initial active tab id when uncontrolled (defaults to the first tab). */
  defaultTab?: TId;
  /** Callback when active tab changes */
  onChange?: (tabId: TId) => void;
  /** Render prop alternative to tab.content */
  children?: (activeTabId: TId) => ReactNode;
  /** Additional class name for styling overrides */
  className?: string;
  /** data-testid applied to the tab list (role="tablist"). */
  listTestId?: string;
  /**
   * Tooltip style for the hover label shown on inactive icon-only tabs.
   * Defaults to `COMPACT_TOOLTIP_STYLE`; pass `MODAL_TOOLTIP_STYLE` inside modals.
   */
  tooltipStyle?: CSSProperties;
  /**
   * Optional content pinned to the right of the tab row, on the same line as the
   * tabs. It stays fixed when the active tab changes (it lives in the header, not
   * the panel). Use for a persistent action like a primary CTA.
   */
  headerRight?: ReactNode;
}

export default function Tabs<TId extends string = string>({
  tabs,
  variant = 'default',
  activeTab,
  defaultTab,
  onChange,
  children,
  className,
  listTestId,
  tooltipStyle,
  headerRight,
}: TabsProps<TId>) {
  const [internalActive, setInternalActive] = useState<TId | undefined>(
    defaultTab ?? tabs[0]?.id,
  );
  const isControlled = activeTab !== undefined;
  const currentTab = isControlled ? activeTab : internalActive;
  const isIconOnly = variant === 'icon-only';
  const tooltipId = useId();

  const handleTabClick = useCallback(
    (tabId: TId) => {
      if (!isControlled) setInternalActive(tabId);
      onChange?.(tabId);
    },
    [isControlled, onChange],
  );

  const activeContent = tabs.find(t => t.id === currentTab)?.content;

  return (
    <TabsContainer className={className}>
      <TabHeader>
        <TabList role="tablist" data-testid={listTestId} $iconOnly={isIconOnly}>
          {tabs.map(tab => {
            const isActive = currentTab === tab.id;
            // In icon-only mode the label is hidden unless the tab is active (or
            // has no icon to fall back on); its name surfaces via a hover tooltip.
            const showLabel = !isIconOnly || isActive || !tab.icon;
            const showTooltip = isIconOnly && !isActive && !!tab.icon;
            // Each tab gets its OWN tooltip id. A single shared id makes
            // react-tooltip re-anchor to the last remaining anchor when the
            // clicked tab drops its anchor — surfacing the wrong tooltip on click.
            const tabTooltipId = showTooltip
              ? `${tooltipId}-${tab.id}`
              : undefined;
            return (
              <TabButton
                key={tab.id}
                role="tab"
                type="button"
                aria-selected={isActive}
                aria-label={showLabel ? undefined : tab.label}
                data-testid={tab.testId}
                data-tooltip-id={tabTooltipId}
                data-tooltip-content={tabTooltipId ? tab.label : undefined}
                $isActive={isActive}
                $iconOnly={isIconOnly}
                onClick={() => handleTabClick(tab.id)}
                tabIndex={isActive ? 0 : -1}
              >
                {tab.icon}
                {isIconOnly ? (
                  <AnimatePresence initial={false}>
                    {showLabel && (
                      <motion.span
                        key="label"
                        variants={LABEL_VARIANTS}
                        initial="hidden"
                        animate="shown"
                        exit="hidden"
                        transition={SIDEBAR_SPRING}
                        style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
                      >
                        {tab.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                ) : (
                  <span>{tab.label}</span>
                )}
                {!isIconOnly && isActive && <ActiveIndicator />}
              </TabButton>
            );
          })}
        </TabList>
        {headerRight && <TabHeaderRight>{headerRight}</TabHeaderRight>}
      </TabHeader>
      {isIconOnly &&
        tabs.map(tab =>
          currentTab !== tab.id && tab.icon ? (
            <Tooltip
              key={tab.id}
              id={`${tooltipId}-${tab.id}`}
              place="bottom"
              positionStrategy="fixed"
              style={tooltipStyle ?? COMPACT_TOOLTIP_STYLE}
            />
          ) : null,
        )}
      <TabContent role="tabpanel">
        {children
          ? currentTab !== undefined
            ? children(currentTab)
            : null
          : activeContent}
      </TabContent>
    </TabsContainer>
  );
}

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
`;

const TabHeader = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-subtle);
`;

const TabHeaderRight = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: var(--spacing-sm);
`;

const TabList = styled.div<{ $iconOnly: boolean }>`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${props =>
    props.$iconOnly
      ? css`
          gap: var(--spacing-xs);
          padding: var(--spacing-xs) 0;
        `
      : css`
          gap: var(--spacing-lg);
          padding: 0 var(--spacing-sm);
        `}
`;

const TabButton = styled.button<{ $isActive: boolean; $iconOnly: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-primary);
  line-height: var(--leading-text-sm);
  white-space: nowrap;
  color: ${props =>
    props.$isActive ? 'var(--foreground)' : 'var(--text-secondary)'};
  font-weight: ${props =>
    props.$isActive
      ? 'var(--font-weight-medium)'
      : 'var(--font-weight-normal)'};
  transition:
    color var(--transition-fast),
    background-color var(--transition-fast);

  svg {
    flex-shrink: 0;
    width: ${props => (props.$iconOnly ? '16px' : '14px')};
    height: ${props => (props.$iconOnly ? '16px' : '14px')};
    opacity: ${props => (props.$isActive ? 1 : 0.6)};
    transition: opacity var(--transition-fast);
  }

  &:hover {
    color: var(--foreground);

    svg {
      opacity: 1;
    }
  }

  ${props =>
    props.$iconOnly
      ? css`
          font-size: var(--text-sm);
          padding: var(--spacing-xs) var(--spacing-sm);
          border-radius: var(--radius-input);

          &:hover {
            background-color: var(--surface-elevated);
          }

          ${props.$isActive &&
          css`
            background-color: var(--surface-elevated);
          `}
        `
      : css`
          font-size: var(--text-sm);
          padding: var(--spacing-sm) 0;
        `}
`;

const ActiveIndicator = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: var(--spacing-2xs);
  background-color: var(--accent);
  border-radius: 1px;
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-top: var(--spacing-md);
`;
